import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, KeyRound } from "lucide-react";
import { useAdminAuth } from "@/lib/admin-auth";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { admin, login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (admin) navigate("/owner/dashboard"); }, [admin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      await login(email.trim(), password, totp.trim());
      navigate("/owner/dashboard");
    } catch (e2) {
      const code = e2.body?.detail?.error;
      if (code === "totp_required" || code === "totp_invalid") {
        setNeedTotp(true);
        setError(code === "totp_invalid" ? "Invalid authenticator code" : null);
      } else {
        setError(e2.message || "Invalid admin credentials");
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-midnight px-6">
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-midnight-2 p-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/10"><ShieldCheck className="h-5 w-5 text-brand" /></div>
          <div><p className="font-display text-lg font-bold tracking-widest text-white">OWNER PORTAL</p><p className="text-xs text-white/50">Pro Garages platform admin</p></div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Owner email" data-testid="admin-email" required disabled={needTotp} className="w-full rounded-lg border border-white/15 bg-white/5 px-3.5 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-brand disabled:opacity-50" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Password" data-testid="admin-password" required disabled={needTotp} className="w-full rounded-lg border border-white/15 bg-white/5 px-3.5 py-3 text-sm text-white placeholder:text-white/40 outline-none focus:border-brand disabled:opacity-50" />
          {needTotp && (
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-white/70"><KeyRound className="h-3.5 w-3.5" />Authenticator code</label>
              <input value={totp} onChange={(e) => setTotp(e.target.value)} inputMode="numeric" placeholder="123456" data-testid="admin-totp" autoFocus className="w-full rounded-lg border border-white/15 bg-white/5 px-3.5 py-3 text-center text-lg tracking-[0.3em] text-white placeholder:text-white/30 outline-none focus:border-brand" />
            </div>
          )}
          {error && <p data-testid="admin-error" className="rounded-md bg-danger/20 px-3 py-2 text-sm text-red-300">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-widest text-white disabled:opacity-60">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : needTotp ? "VERIFY & SIGN IN" : "SIGN IN"}
          </button>
        </form>
      </div>
    </div>
  );
}
